import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from 'common-src/components/base/Button';
import { cachePortalInvites, cachePortalRelationStatuses } from 'common-src/features/cache/actions';
import {
  getCachedPortalInvites,
  getCachedPortalRelationStatuses,
} from 'common-src/features/cache/selectors';
import { apiRequest } from 'common-src/features/rest';
import {
  getPatientCaregivers,
  getPatientCaregiversRequest,
} from 'common-src/models/PatientCaregiver';
import { ColorsNew } from 'common-src/styles';

import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import { CognitoStatusEnum, relationStatuses } from '../Security/constants';
import { InviteStatus, RelationStatus } from './constants';
import styles from './PortalCaregivers.module.scss';
import { getErrorMessage } from './utils';

const PortalCaregivers = ({ patientId, portalPatientId }) => {
  const dispatch = useDispatch();
  const cachedInvites = useCustomSelector((state) =>
    getCachedPortalInvites(state, portalPatientId),
  );
  const cachedRelationStatuses = useCustomSelector((state) =>
    getCachedPortalRelationStatuses(state, portalPatientId),
  );
  const [invites, setInvites] = useState(cachedInvites || []);
  const [relationStatusesData, setRelationStatusesData] = useState(cachedRelationStatuses || []);
  const [isLoading, setIsLoading] = useState(!cachedInvites);
  const [isRelationStatusesLoading, setIsRelationStatusesLoading] =
    useState(!cachedRelationStatuses);
  const [isCaregiversLoading, setIsCaregiversLoading] = useState(true);
  const [caregiverActionLoading, setCaregiverActionLoading] = useState({});
  const [inviteActionLoading, setInviteActionLoading] = useState({});

  useEffect(() => {
    setIsCaregiversLoading(true);
    const fetchCaregivers = async () => {
      dispatch(getPatientCaregiversRequest({ patientId }));
      setIsCaregiversLoading(false);
    };
    fetchCaregivers();
  }, [patientId, dispatch]);
  const { showAlert, AlertType } = useAlert();

  useEffect(() => {
    if (cachedInvites) {
      return;
    }

    const fetchInvites = async () => {
      setIsLoading(true);
      try {
        const response = await apiRequest({
          endpoint: 'patientPortalBackend/pendingInvites',
          method: 'POST',
          body: { patient_id: portalPatientId },
        });
        if (response.ok) {
          const data = await response.json();
          const fetchedInvites = data.invites || [];
          setInvites(fetchedInvites);
          dispatch(cachePortalInvites(portalPatientId, fetchedInvites));
        } else {
          const errorMessage = await getErrorMessage(response, 'Failed to fetch pending invites.');
          showAlert(AlertType.Error, 'Error', errorMessage);
        }
      } catch (error) {
        showAlert(
          AlertType.Error,
          'Error',
          error.message || 'Network error while fetching invites.',
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchInvites();
  }, [portalPatientId, cachedInvites, dispatch]);

  useEffect(() => {
    if (cachedRelationStatuses) {
      return;
    }

    const fetchRelationStatuses = async () => {
      setIsRelationStatusesLoading(true);
      try {
        const response = await apiRequest({
          endpoint: 'patientPortalBackend/relationStatuses',
          method: 'POST',
          body: { patient_id: portalPatientId },
        });
        if (response.ok) {
          const data = await response.json();
          const fetchedRelationStatuses = data.relations || [];
          setRelationStatusesData(fetchedRelationStatuses);
          dispatch(cachePortalRelationStatuses(portalPatientId, fetchedRelationStatuses));
        } else {
          const errorMessage = await getErrorMessage(
            response,
            'Failed to fetch relation statuses.',
          );
          showAlert(AlertType.Error, 'Error', errorMessage);
        }
      } catch (error) {
        showAlert(
          AlertType.Error,
          'Error',
          error.message || 'Network error while fetching relation statuses.',
        );
      } finally {
        setIsRelationStatusesLoading(false);
      }
    };
    fetchRelationStatuses();
  }, [portalPatientId, cachedRelationStatuses, dispatch]);

  const handleStatusUpdate = async (inviteId, status) => {
    setInviteActionLoading((prev) => ({ ...prev, [inviteId]: status }));
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/updateInviteStatus',
        method: 'PATCH',
        body: { invite_id: inviteId, status },
      });
      if (response.ok) {
        const updatedInvites = invites.filter((invite) => invite.invite_id !== inviteId);
        setInvites(updatedInvites);
        dispatch(cachePortalInvites(portalPatientId, updatedInvites));
        showAlert(AlertType.Success, 'Success', `Invite ${status.toLowerCase()} successfully.`);
      } else {
        const errorMessage = await getErrorMessage(response, 'Failed to update invite status.');
        showAlert(AlertType.Error, 'Error', errorMessage);
      }
    } catch (error) {
      showAlert(
        AlertType.Error,
        'Error',
        error.message || 'Network error while updating invite status.',
      );
    } finally {
      setInviteActionLoading((prev) => ({ ...prev, [inviteId]: undefined }));
    }
  };

  const handleApprove = (inviteId) => {
    handleStatusUpdate(inviteId, InviteStatus.ACCEPTED);
  };

  const handleDecline = (inviteId) => {
    handleStatusUpdate(inviteId, InviteStatus.DECLINED);
  };

  const handleCreateAccess = async (caregiverId) => {
    setCaregiverActionLoading((prev) => ({ ...prev, [caregiverId]: 'create' }));
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/createCaregiverPortalAccess',
        method: 'POST',
        body: { patient_id: patientId, caregiver_id: caregiverId },
      });
      if (response.ok) {
        const data = await response.json();
        showAlert(
          AlertType.Success,
          'Success',
          data.message || 'Caregiver portal access created successfully.',
        );
        dispatch(cachePortalInvites(portalPatientId, null));
        dispatch(getPatientCaregiversRequest({ patientId }));
      } else {
        const errorMessage = await getErrorMessage(
          response,
          'Failed to create caregiver portal access.',
        );
        showAlert(AlertType.Error, 'Error', errorMessage);
      }
    } catch (error) {
      showAlert(
        AlertType.Error,
        'Error',
        error.message || 'Network error while creating caregiver portal access.',
      );
    } finally {
      setCaregiverActionLoading((prev) => ({ ...prev, [caregiverId]: undefined }));
    }
  };

  const handleToggleAccess = async (mindspanCaregiverId, status) => {
    setCaregiverActionLoading((prev) => ({ ...prev, [mindspanCaregiverId]: 'toggle' }));
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/enableOrDisableAccess',
        method: 'POST',
        body: { send_email: false, caregiver_id: mindspanCaregiverId, status },
      });
      if (response.ok) {
        const data = await response.json();
        const statusText = status === CognitoStatusEnum.ACTIVE ? 'enabled' : 'disabled';
        showAlert(
          AlertType.Success,
          'Success',
          data.message || `Caregiver access ${statusText} successfully.`,
        );
        dispatch(getPatientCaregiversRequest({ patientId }));
      } else {
        const errorMessage = await getErrorMessage(
          response,
          'Failed to update caregiver access status.',
        );
        showAlert(AlertType.Error, 'Error', errorMessage);
      }
    } catch (error) {
      showAlert(
        AlertType.Error,
        'Error',
        error.message || 'Network error while updating caregiver access status.',
      );
    } finally {
      setCaregiverActionLoading((prev) => ({ ...prev, [mindspanCaregiverId]: undefined }));
    }
  };

  const handleUnlinkCaregiver = async (mindspanCaregiverId) => {
    setCaregiverActionLoading((prev) => ({ ...prev, [mindspanCaregiverId]: 'unlink' }));
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/setRelationStatus',
        method: 'POST',
        body: {
          patient_id: portalPatientId,
          caregiver_id: mindspanCaregiverId,
          status: RelationStatus.REMOVED,
        },
      });
      if (response.ok) {
        const data = await response.json();
        showAlert(AlertType.Success, 'Success', data.message || 'Caregiver unlinked successfully.');
        dispatch(getPatientCaregiversRequest({ patientId }));
        const statusResponse = await apiRequest({
          endpoint: 'patientPortalBackend/relationStatuses',
          method: 'POST',
          body: { patient_id: portalPatientId },
        });
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          const fetchedRelationStatuses = statusData.relations || [];
          setRelationStatusesData(fetchedRelationStatuses);
          dispatch(cachePortalRelationStatuses(portalPatientId, fetchedRelationStatuses));
        }
      } else {
        const errorMessage = await getErrorMessage(response, 'Failed to unlink caregiver.');
        showAlert(AlertType.Error, 'Error', errorMessage);
      }
    } catch (error) {
      showAlert(
        AlertType.Error,
        'Error',
        error.message || 'Network error while unlinking caregiver.',
      );
    } finally {
      setCaregiverActionLoading((prev) => ({ ...prev, [mindspanCaregiverId]: undefined }));
    }
  };

  const caregivers = useCustomSelector((state) => getPatientCaregivers(state, patientId));

  const filteredCaregivers = caregivers.filter((cg) => {
    if (!cg.mindspanCaregiverId) return true;

    const relationStatus = relationStatusesData.find(
      (relation) => relation.caregiver_id === cg.mindspanCaregiverId,
    );

    return !relationStatus || relationStatus.status !== relationStatuses.REMOVED;
  });

  return (
    <article className={[styles.article, 'flex-column', 'gap-16'].join(' ')}>
      <h2 className={styles.heading}>Invites</h2>
      <div className={styles.inviteList}>
        {isLoading ? (
          <div className={styles.empty}>Loading invites...</div>
        ) : invites.length === 0 ? (
          <div className={styles.empty}>No pending invites.</div>
        ) : (
          invites.map((invite) => (
            <div key={invite.invite_id} className={styles.inviteRow}>
              <div className={styles.info}>
                <span className={styles.name}>
                  {invite.caregiver?.first_name} {invite.caregiver?.last_name}
                </span>
                <span className={styles.email}>{invite.caregiver?.email}</span>
              </div>
              <div className={styles.actions}>
                <Button
                  text="Approve"
                  backgroundColor={ColorsNew.darkGreen}
                  textColor={ColorsNew.white}
                  classNames={[styles.approveBtn]}
                  onClick={() => handleApprove(invite.invite_id)}
                  disabled={!!inviteActionLoading[invite.invite_id]}
                  isLoading={inviteActionLoading[invite.invite_id] === InviteStatus.ACCEPTED}
                />
                <Button
                  text="Decline"
                  backgroundColor={ColorsNew.mediumRed}
                  borderColor={ColorsNew.mediumRed}
                  textColor={ColorsNew.white}
                  classNames={[styles.declineBtn]}
                  onClick={() => handleDecline(invite.invite_id)}
                  disabled={!!inviteActionLoading[invite.invite_id]}
                  isLoading={inviteActionLoading[invite.invite_id] === InviteStatus.DECLINED}
                />
              </div>
            </div>
          ))
        )}
      </div>

      <h2 className={styles.heading}>Your Caregivers</h2>
      <div className={styles.inviteList}>
        {isCaregiversLoading || isRelationStatusesLoading ? (
          <div className={styles.empty}>Loading caregivers...</div>
        ) : filteredCaregivers.length === 0 ? (
          <div className={styles.empty}>No caregivers found.</div>
        ) : (
          filteredCaregivers.map((cg) => (
            <div key={cg.id} className={styles.inviteRow}>
              <div className={styles.info}>
                <span className={styles.name}>
                  {cg.firstName} {cg.lastName}
                </span>
                <span className={styles.email}>{cg.email}</span>
              </div>
              <div className={styles.actions}>
                <Button
                  text="Create Access"
                  backgroundColor={ColorsNew.darkGreen}
                  textColor={ColorsNew.white}
                  classNames={[styles.approveBtn]}
                  onClick={() => handleCreateAccess(cg.id)}
                  disabled={
                    !!cg.mindspanCaregiverId ||
                    caregiverActionLoading[cg.id] === 'create' ||
                    caregiverActionLoading[cg.mindspanCaregiverId] === 'toggle' ||
                    caregiverActionLoading[cg.mindspanCaregiverId] === 'unlink'
                  }
                  isLoading={caregiverActionLoading[cg.id] === 'create'}
                />
                {cg.mindspanCaregiverCognitoStatus === CognitoStatusEnum.ACTIVE ? (
                  <Button
                    text="Disable Access"
                    backgroundColor={ColorsNew.mediumRed}
                    borderColor={ColorsNew.mediumRed}
                    textColor={ColorsNew.white}
                    classNames={[styles.approveBtn]}
                    onClick={() =>
                      handleToggleAccess(cg.mindspanCaregiverId, CognitoStatusEnum.INACTIVE)
                    }
                    disabled={
                      !cg.mindspanCaregiverId ||
                      caregiverActionLoading[cg.id] === 'create' ||
                      caregiverActionLoading[cg.mindspanCaregiverId] === 'toggle' ||
                      caregiverActionLoading[cg.mindspanCaregiverId] === 'unlink'
                    }
                    isLoading={caregiverActionLoading[cg.mindspanCaregiverId] === 'toggle'}
                  />
                ) : (
                  <Button
                    text="Enable Access"
                    backgroundColor={ColorsNew.darkGreen}
                    borderColor={ColorsNew.darkGreen}
                    textColor={ColorsNew.white}
                    classNames={[styles.approveBtn]}
                    onClick={() =>
                      handleToggleAccess(cg.mindspanCaregiverId, CognitoStatusEnum.ACTIVE)
                    }
                    disabled={
                      !cg.mindspanCaregiverId ||
                      caregiverActionLoading[cg.id] === 'create' ||
                      caregiverActionLoading[cg.mindspanCaregiverId] === 'toggle' ||
                      caregiverActionLoading[cg.mindspanCaregiverId] === 'unlink'
                    }
                    isLoading={caregiverActionLoading[cg.mindspanCaregiverId] === 'toggle'}
                  />
                )}
                <Button
                  text="Unlink Caregiver"
                  backgroundColor={ColorsNew.darkGreen}
                  textColor={ColorsNew.white}
                  classNames={[styles.approveBtn]}
                  onClick={() => handleUnlinkCaregiver(cg.mindspanCaregiverId)}
                  disabled={
                    !cg.mindspanCaregiverId ||
                    caregiverActionLoading[cg.id] === 'create' ||
                    caregiverActionLoading[cg.mindspanCaregiverId] === 'toggle' ||
                    caregiverActionLoading[cg.mindspanCaregiverId] === 'unlink'
                  }
                  isLoading={caregiverActionLoading[cg.mindspanCaregiverId] === 'unlink'}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </article>
  );
};

PortalCaregivers.propTypes = {
  portalPatientId: PropTypes.string,
  patientId: PropTypes.number,
};

export default PortalCaregivers;
